import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FaLeaf, FaPills, FaBox, FaIndustry, FaArrowLeft } from 'react-icons/fa';
import EnquiryForm from '../components/EnquiryForm';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0F5132] border-t-transparent"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0F5132] mb-4">Product not found</h2>
          <Link to="/products" className="text-[#0F5132] hover:underline">Back to Products</Link>
        </div>
      </div>
    );
  }

  const CategoryIcon = product.category === 'Herbal' ? FaLeaf : FaPills;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="product-detail-page">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#0F5132] to-[#0F5132] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/products" className="inline-flex items-center space-x-2 text-white/80 hover:text-white mb-6 transition-colors">
            <FaArrowLeft />
            <span>Back to Products</span>
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">{product.name}</h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Info Card */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-[#0F5132] rounded-xl flex items-center justify-center">
                  <CategoryIcon className="text-[#0F5132] text-3xl" />
                </div>
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      product.category === 'Herbal' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {product.category}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 text-[#0F5132] rounded text-sm font-medium">
                      {product.subcategory}
                    </span>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-[#0F5132] mb-6">Composition</h2>
              <p className="text-[#0F5132] leading-relaxed mb-8">
                {product.composition}
              </p>

              <h2 className="text-2xl font-bold text-[#0F5132] mb-6">Benefits</h2>
              <p className="text-[#0F5132] leading-relaxed">
                {product.benefits}
              </p>
            </div>

            {/* Packaging */}
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <FaBox className="text-[#0F5132] text-2xl" />
                  <h3 className="text-xl font-bold text-[#0F5132]">Available Packaging Sizes</h3>
                </div>
                <div className="space-y-2">
                  {product.packaging_sizes.map((size, index) => (
                    <div key={index} className="px-3 py-2 bg-gray-50 rounded-lg text-[#0F5132]">
                      {size}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Enquiry Form */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-gradient-to-br from-[#0F5132] to-[#0F5132] rounded-xl p-6 text-white mb-6">
                <h3 className="text-2xl font-bold mb-3">Interested in this product?</h3>
                <p className="text-gray-100 mb-4">Get a custom quote and detailed information</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-[#0F5132] rounded-full"></span>
                    <span>Custom formulation available</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-[#0F5132] rounded-full"></span>
                    <span>Flexible packaging options</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="w-2 h-2 bg-[#0F5132] rounded-full"></span>
                    <span>Complete regulatory support</span>
                  </div>
                </div>
              </div>
              
              <EnquiryForm title="Request a Quote" context="manufacturing" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;